import { Injectable } from '@angular/core';
import { isEmpty, isNil, find, findLast, chain } from 'lodash';

import { comparators } from '../../core/comparators';
import { LabTestResult } from '../../lab-test-results/lab-test-result';
import { LabTestResultGridRowItem } from '../grid-row-item';
import { StandardTestResultsItem } from './standard-test-results.component';
import { SubjectColumnParams } from './osr-subject-columns-renderer.component';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import { TestResult } from '../../lab-test-results/test-result';
import { TextListItem, TextListParams } from '../../covance-ag-grid';
import { TestResultsParams } from '../test-result-ag-renderer.component';
import { LabTestSortArgs } from '../../lab-test-results/lab-test-sort-info';
import { ICellRendererParams } from 'ag-grid-community/main';

interface ICellRendererParamsTyped<T> extends ICellRendererParams {
    data: T;
}

@Injectable()
export class StandardTestResultHelpers {
    public getUnits(res: LabTestResult[], params: LabTestResultGridRowItem) {
        if (!params.data) {
            return null;
        }

        const labTestResults = this.getLabTestResults(res, params);
        const testWithUnit = findLast(labTestResults, (item: LabTestResult) => item.currentResult && item.currentResult.unit);

        return testWithUnit ? testWithUnit.currentResult.unit : '';
    }

    public getUnitsForTestResultExport(res: LabTestResult[], params: LabTestResultGridRowItem) {
        if (!params.data) {
            return null;
        }

        const labTestResults = this.getLabTestResultsForExport(res, params);
        const testWithUnit = findLast(labTestResults, (item: LabTestResult) => item.currentResult && item.currentResult.unit);

        return testWithUnit ? testWithUnit.currentResult.unit : '';
    }

    public getRangeRenderer(res: LabTestResult[], params: LabTestResultGridRowItem): string {
        if (!params || !params.node || !params.data) {
            return null;
        }
        if (params.node.group) {
            return '';
        }

        const labTestResults = this.getLabTestResults(res, params);
        const testWithResult = findLast(labTestResults, (item: LabTestResult) => item.currentResult && item.currentResult.range);

        return testWithResult ? testWithResult.currentResult.range : '';
    }

    public getRangeRendererForTestResultExport(res: LabTestResult[], params: LabTestResultGridRowItem): string {
        if (!params || !params.node || !params.data) {
            return null;
        }
        if (params.node.group) {
            return '';
        }

        const labTestResults = this.getLabTestResultsForExport(res, params);
        const testWithResult = findLast(labTestResults, (item: LabTestResult) => item.currentResult && item.currentResult.range);

        return testWithResult ? testWithResult.currentResult.range : '';
    }

    public getDataRevision<T>(params: ICellRendererParamsTyped<T>, hasDataRevision: (labTest: T) => boolean) {
        if (!params || !params.node || !params.data) {
            return '';
        }
        return hasDataRevision(params.data) ? 'R' : '';
    }

    public sortLabTestResultData(a: LabTestResult, b: LabTestResult) {
        if (!a && !b) {
            return 0;
        }
        if (!a) {
            return 1;
        }
        if (!b) {
            return -1;
        }
        
        let result = comparators.dateTimeComparator(b.visitCollectionDate, a.visitCollectionDate);

        if (result === 0) {
            result = comparators.numberComparator(a.accessionId, b.accessionId);

            if (result === 0) {
                result = comparators.stringComparator(a.testGroup, b.testGroup);

                if (result === 0) {
                    result = comparators.numberComparator(a.testSequence, b.testSequence);

                    if (result === 0) {
                        const aLabTestResult = a.currentResult;
                        const bLabTestResult = b.currentResult;
                        if (aLabTestResult && bLabTestResult) {
                            result = comparators.numberComparator(aLabTestResult.sequence, bLabTestResult.sequence);
                        }
                    }
                }
            }
        }
        return result;
    }

    public getResultRenderer(getResult: (res: TestResult) => string) {
        const renderrer = (params: StandardTestResultsItem) => {
            if (params.node.group) {
                return '';
            }
            const labTestResult = find(params.data.labTestResults, res => res.currentResult !== null);
            if (!labTestResult) {
                return '';
            }

            if (!labTestResult.currentResult === null) {
                return '';
            }

            return getResult(labTestResult.currentResult);
        };
        return renderrer;
    }

    public commentsRenderer(x: { [index: string]: string }) {
        if (!x) {
            return null;
        }
        return new TextListParams(Object.keys(x).map(c => c.toUpperCase())
            .map(c => ({
                text: c,
                tooltip: c
            } as TextListItem)));
    }

    public getAdminQuestionRenderer(params: LabTestResultGridRowItem) {
        if (!params || !params.node || !params.data) {
            return '';
        }
        return params.data.isQuestion ? 'Y' : '';
    }

    public getDisplayValue(testResultsParams: TestResultsParams) {
        if (!testResultsParams || isEmpty(testResultsParams.labTestResults)) {
            return null;
        }
        const result = testResultsParams.labTestResults[0];
        return result.isCancelled ? null : result.displayValue;
    }

    public getCombinedLabTestResultsUniqKey(res: LabTestResult) {
        return res.ctSubjectId + ':'
            + res.uniqLabTestIdKey + ':'
            + res.accessionId + ':'
            + res.visitTestId + ':'
            + res.visitCollectionDateValue;
    }

    public sortSubjects(a: SubjectColumnParams, b: SubjectColumnParams): number {
        const valueAMissing = isNil(a);
        const valueBMissing = isNil(b);

        if (valueAMissing && valueBMissing) {
            return 0;
        }
        if (valueAMissing) {
            return -1;
        }
        if (valueBMissing) {
            return 1;
        }

        let result = comparators.stringComparator(a.subjectNumberAndInitials, b.subjectNumberAndInitials);
        if (result === 0) {
            result = comparators.stringComparator(a.screeningNumber, b.screeningNumber);
            if (result === 0) {
                result = comparators.numberComparator(a.age, b.age);
                if (result === 0) {
                    result = comparators.stringComparator(a.gender, b.gender);
                    if (result === 0) {
                        result = comparators.numberComparator(a.exportSortId, b.exportSortId);
                    }
                }
            }
        }
        return result;
    }

    public getSubjectInfo(params: StandardTestResultsItem, routerLink: any[]): SubjectColumnParams {
        const data = params.data;
        if (!data) {
            return null;
        }
        const subjectNumberAndInitials = SubjectTitleRenderer.renderTitle(params);
        const subjectInfo: string[] = [];
        if (!!subjectNumberAndInitials) {
            subjectInfo.push(subjectNumberAndInitials);
            if (subjectNumberAndInitials !== SubjectTitleRenderer.removedSubjectLabel) {
                if (!!data.screeningNumber) {
                    subjectInfo.push(`(${data.screeningNumber})`);
                }
                if (!isNil(data.age) && !data.isAgeProject) {
                    subjectInfo.push(`${data.age} ${data.ageUnit}`);
                }
                if (!isNil(data.ageProjectDisplayValue) && data.isAgeProject) {
                    subjectInfo.push(`${data.ageProjectDisplayValue} `);
                }
                if (!!data.sex) {
                    subjectInfo.push(data.sex);
                }
            }
        }

        const displayText = subjectInfo.join(' ');
        const result = {
            routerLink: routerLink,
            screeningNumber: data.screeningNumber,
            subjectNumberAndInitials: subjectNumberAndInitials,
            age: data.age,
            ageProjectDisplayValue: data.ageProjectDisplayValue,
            gender: data.sex,
            hasSubjectDataRevision: data.hasSubjectDataRevision,
            text: displayText,
            toString: () => displayText,
            data: data
        };
        return result;
    }

    private getLabTestResults(res: LabTestResult[], params: LabTestResultGridRowItem): LabTestResult[] {
        const testId = params.data.testId;
        const testGroup = params.data.testGroup;
        const uniqLabTestIdKey = params.data.uniqLabTestIdKey;

        if (!res || isNil(testId) || isNil(testGroup)) {
            return [];
        }

        const sortInfoArgs: LabTestSortArgs = ['testId', 'testGroup', 'visitCollectionDate'];
        const testResults = chain(res)
            .filter(item =>
                item.uniqLabTestIdKey === uniqLabTestIdKey &&
                item.testId === testId &&
                item.testGroup === testGroup &&
                item.currentResult !== null)
            .sortBy(sortInfoArgs)
            .value();

        return testResults || [];
    }

    private getLabTestResultsForExport(res: LabTestResult[], params: LabTestResultGridRowItem): LabTestResult[] {
        const testId = params.data.testId;
        const testGroup = params.data.testGroup;
        const uniqLabTestIdKey = params.data.uniqLabTestIdKey;
        const accessionId = params.data.accessionId;

        if (!res || isNil(testId) || isNil(testGroup)) {
            return [];
        }

        const sortInfoArgs: LabTestSortArgs = ['testId', 'testGroup', 'visitCollectionDate'];
        const testResults = chain(res)
            .filter(item =>
                item.uniqLabTestIdKey === uniqLabTestIdKey &&
                item.accessionId === accessionId &&
                item.testId === testId &&
                item.testGroup === testGroup &&
                item.currentResult !== null)
            .sortBy(sortInfoArgs)
            .value();

        return testResults || [];
    }
}
